import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import { BTSwitch } from "commonComponents/btWrappers/BTSwitch/BTSwitch";
import { useInfoArchOptIn } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/useInfoArchOptIn";

interface IInfoArchOptInToggleProps {
    /**
     * Label rendered to the left of the switch.
     * @default "Research preview"
     */
    label?: string;
    /**
     * Distinguishes the DOM `id`/`data-testid` when more than one instance can be mounted at
     * once — e.g. the persistent `MainNavigation` toggle and this component's copy in
     * `BTVerticalNav`'s `bottom` slot both render for an opted-in builder user.
     *
     * This value also becomes the `uniqueId` on the `ValueChange` event `BTSwitch` sends to
     * Mixpanel, which is the only property the Mixpanel dashboard identifies the toggle by. A new
     * prefix therefore starts a separate series there rather than extending the existing one.
     * @default "infoArchOptIn"
     */
    testIdPrefix?: string;
    /**
     * Overrides the label's default `text-white opacity-85` styling (written for
     * `MainNavigation`'s dark bar) via `cn`/tailwind-merge — e.g. `BTVerticalNav`'s white
     * sidebar passes `text-fg-secondary opacity-100` to fully replace both utilities rather than
     * layering a new color under the old opacity.
     */
    labelClassName?: string;
}

/**
 * Persistent opt in / opt out control for the Research Preview information architecture.
 *
 * Reads and writes through `useInfoArchOptIn`, which is also what the Research Preview shell
 * itself uses to gate rendering — this keeps the toggle and the shell agreeing on what "opted
 * in" means with a single implementation. That hook also emits the Datadog RUM action, so any
 * future caller of it is instrumented without having to remember to be.
 *
 * Rendered as a binary Switch with `label` to its left. Callers drop this directly into a
 * ReactNode slot (e.g. `BTVerticalNav`'s `bottom`) — it needs no props of its own to know what
 * opting in means, aside from `labelClassName`/`testIdPrefix` to fit whatever surface it's
 * placed on.
 */
export const InfoArchOptInToggle = ({
    label = "Research preview",
    testIdPrefix = "infoArchOptIn",
    labelClassName,
}: IInfoArchOptInToggleProps) => {
    const { isOptedIn, isPending, setOptedIn } = useInfoArchOptIn();

    return (
        <div
            className="flex items-center justify-between gap-2 px-3"
            data-testid={`${testIdPrefix}ToggleWrapper`}
        >
            <BTText className={cn("text-nowrap text-base text-white opacity-85", labelClassName)}>
                {label}
            </BTText>
            <BTSwitch
                id={testIdPrefix}
                data-testid={testIdPrefix}
                checked={isOptedIn}
                disabled={isPending}
                size="small"
                onCheckedChange={(_id, value) => setOptedIn(value)}
            />
        </div>
    );
};
